import { Extension, getNodeType } from '@tiptap/core';
import { isChangeOrigin } from '@tiptap/extension-collaboration';
import { Mapping } from '@tiptap/pm/transform';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import type { Node as PMNode, NodeType } from '@tiptap/pm/model';

export interface AutoJoinerOptions {
  /** Node type names whose adjacent same-type siblings should auto-merge (e.g. bulletList, orderedList). */
  nodeTypes: string[];
}

interface Range {
  from: number;
  to: number;
}

/**
 * Collaboration's sync plugin (@tiptap/y-tiptap) tags every transaction it
 * builds from Yjs (initial load, a remote update, a late sync) with its
 * plugin key. Normalising that content would be sent back to the server as
 * this client's own edit.
 */
export function isFromYjs(tr: Transaction): boolean {
  return isChangeOrigin(tr);
}

/** The ranges the user's own transactions changed, mapped into the final doc. */
function userChangedRanges(transactions: readonly Transaction[]): Range[] {
  const all = new Mapping();
  const pending: Array<{ range: Range; mapFrom: number }> = [];
  for (const tr of transactions) {
    const user = tr.docChanged && !isFromYjs(tr);
    for (const map of tr.mapping.maps) {
      all.appendMap(map);
      if (!user) continue;
      const mapFrom = all.maps.length;
      map.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
        pending.push({ range: { from: newStart, to: newEnd }, mapFrom });
      });
    }
  }
  return pending.map(({ range, mapFrom }) => {
    const rest = all.slice(mapFrom);
    return { from: rest.map(range.from, -1), to: rest.map(range.to, 1) };
  });
}

/** Every boundary between two adjacent same-type siblings of a configured type. */
function joinableBoundaries(doc: PMNode, nodeTypes: NodeType[]): number[] {
  const boundaries: number[] = [];
  const scanChildren = (node: PMNode, base: number) => {
    let childPos = base;
    node.forEach((child, _offset, index) => {
      if (index > 0) {
        const prev = node.child(index - 1);
        if (prev.type === child.type && nodeTypes.some((t) => t === child.type)) {
          boundaries.push(childPos);
        }
      }
      childPos += child.nodeSize;
    });
  };
  scanChildren(doc, 0);
  doc.descendants((node, pos) => {
    scanChildren(node, pos + 1);
  });
  return boundaries;
}

/**
 * Joins adjacent same-type siblings whose boundary lies within (or touches)
 * one of `ranges`. Joins one at a time, last first, re-mapping the ranges
 * through each join. Returns true if it mutated `tr`.
 */
function joinAdjacent(tr: Transaction, nodeTypes: NodeType[], ranges: Range[]): boolean {
  let joined = false;
  let current = ranges;
  for (;;) {
    const boundary = joinableBoundaries(tr.doc, nodeTypes)
      .filter((b) => current.some((r) => b >= r.from && b <= r.to))
      .pop();
    if (boundary === undefined) return joined;
    const $b = tr.doc.resolve(boundary);
    if (!$b.nodeBefore || !$b.nodeAfter) return joined;
    tr.join(boundary);
    joined = true;
    const map = tr.steps[tr.steps.length - 1].getMap();
    current = current.map((r) => ({ from: map.map(r.from, -1), to: map.map(r.to, 1) }));
  }
}

/**
 * Auto-merges adjacent same-type list (or other configured) nodes that the
 * user's own edit brings together: when the content separating two bullet
 * lists is deleted, ProseMirror leaves two adjacent <ul>s; this rejoins them.
 * It never touches content it only received (a load, a remote edit) and does
 * nothing in a read-only view. Ported to nula conventions from the Campsite
 * editor's AutoJoiner (itself the tiptap-extension-auto-joiner pattern).
 */
export const AutoJoinerExtension = Extension.create<AutoJoinerOptions>({
  name: 'autoJoiner',

  addOptions() {
    return { nodeTypes: [] };
  },

  addProseMirrorPlugins() {
    const nodeTypes = this.options.nodeTypes.map((type) => getNodeType(type, this.editor.schema));
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('autoJoiner'),
        appendTransaction: (transactions, _oldState, newState) => {
          if (!editor.isEditable) return null;
          const ranges = userChangedRanges(transactions);
          if (ranges.length === 0) return null;
          const tr = newState.tr;
          return joinAdjacent(tr, nodeTypes, ranges) ? tr : null;
        },
      }),
    ];
  },
});
